import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, File } from 'lucide-react';
import api from '../../lib/api';

interface FileUploaderProps {
  onUpload: (urls: string[]) => void;
  multiple?: boolean;
  accept?: string;
  maxSize?: number; // in MB
}

export default function FileUploader({
  onUpload,
  multiple = false,
  accept = 'image/*,video/*',
  maxSize = 50,
}: FileUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; url: string }[]>([]);
  const [error, setError] = useState('');

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setError('');
      setUploading(true);

      try {
        const uploadPromises = acceptedFiles.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);

          // Determine endpoint based on file type
          let endpoint = '/upload/file';
          if (file.type.startsWith('image/')) {
            endpoint = '/upload/image';
          } else if (file.type.startsWith('video/')) {
            endpoint = '/upload/video';
          }

          const response = await api.post(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          return {
            name: file.name,
            url: response.data.url,
          };
        });

        const results = await Promise.all(uploadPromises);
        setUploadedFiles((prev) => [...prev, ...results]);
        onUpload(results.map((r) => r.url));
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to upload files');
      } finally {
        setUploading(false);
      }
    },
    [onUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    accept: accept
      ? accept.split(',').reduce((acc, type) => ({ ...acc, [type.trim()]: [] }), {})
      : undefined,
    maxSize: maxSize * 1024 * 1024,
  });

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-accent2 bg-accent2/10' : 'border-glass hover:border-glass-strong bg-glass-2'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto mb-4 text-glass-text-3" />
        {isDragActive ? (
          <p className="text-glass-text-2">Drop the files here...</p>
        ) : (
          <div>
            <p className="text-glass-text-2 mb-1">
              Drag & drop {multiple ? 'files' : 'a file'} here, or click to select
            </p>
            <p className="text-sm text-glass-text-3">Max size: {maxSize}MB</p>
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-500 dark:text-rose-300 text-sm">
          {error}
        </div>
      )}

      {uploading && (
        <div className="text-center text-sm text-glass-text-3">Uploading...</div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-glass-text">Uploaded Files</h4>
          {uploadedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 rounded-xl border border-glass bg-glass-2"
            >
              <div className="flex items-center gap-2">
                <File className="w-4 h-4 text-glass-text-3" />
                <span className="text-sm truncate text-glass-text">{file.name}</span>
              </div>
              <button
                onClick={() => removeFile(index)}
                className="text-glass-text-3 hover:text-glass-text"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

